import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Loader2, SendHorizonal, ArrowLeft, ArrowRight, ChevronRight, Info, AlertCircle, Stethoscope } from 'lucide-react'
import { FieldRenderer } from '../../components/public/FieldRenderer'
import { getEncuestaPublica, submitEncuesta } from '../../api/encuestas.api'
import type { EncuestaPublica } from '../../types'

export function EncuestaPublicaPage() {
  const { uuid } = useParams<{ uuid: string }>()
  const navigate = useNavigate()

  const [encuesta, setEncuesta] = useState<EncuestaPublica | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [started, setStarted] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // Multi-step state
  const [currentStep, setCurrentStep] = useState(0) // 0=bienvenida

  const [campoPrincipalValor, setCampoPrincipalValor] = useState('')
  const [respuestas, setRespuestas] = useState<Record<string, string>>({})
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [fieldError, setFieldError] = useState('')

  // Modal instrucciones para multi-step
  const [showInstrucciones, setShowInstrucciones] = useState(false)

  useEffect(() => {
    if (!uuid) return
    let cancelled = false

    async function load() {
      try {
        const data = await getEncuestaPublica(uuid!)
        if (!cancelled) setEncuesta(data)
      } catch (err: unknown) {
        if (cancelled) return
        const axiosErr = err as { response?: { status?: number; data?: { error?: string } } }
        if (axiosErr.response?.status === 400 && axiosErr.response?.data?.error === 'ya_respondida') {
          navigate('/encuestas/ya-respondida', { replace: true })
        } else if (axiosErr.response?.status === 410 || axiosErr.response?.status === 404) {
          navigate('/encuestas/no-disponible', { replace: true })
        } else {
          setError('No se pudo cargar la encuesta')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()
    return () => { cancelled = true }
  }, [uuid, navigate])

  // Favicon y título dinámicos
  useEffect(() => {
    if (!encuesta) return
    document.title = encuesta.titulo

    if (encuesta.imagen) {
      let link = document.querySelector<HTMLLinkElement>("link[rel~='icon']")
      if (!link) {
        link = document.createElement('link')
        link.rel = 'icon'
        document.head.appendChild(link)
      }
      link.href = encuesta.imagen
    }

    return () => { document.title = 'Tonal' }
  }, [encuesta])

  const handleRespuestaChange = (campoId: number, valor: string) => {
    setRespuestas((prev) => ({ ...prev, [String(campoId)]: valor }))
    setFieldErrors((prev) => { const next = { ...prev }; delete next[String(campoId)]; return next })
    setFieldError('')
  }

  // === STANDARD FORM LOGIC ===
  const filledCount = encuesta
    ? encuesta.campos.filter((c) => respuestas[String(c.id)]?.trim()).length
    : 0

  const validate = (): boolean => {
    if (!encuesta) return false
    const errors: Record<string, string> = {}
    for (const campo of encuesta.campos) {
      if (campo.requerido && !respuestas[String(campo.id)]?.trim()) {
        errors[String(campo.id)] = 'Este campo es obligatorio'
      }
      if (campo.tipo === 'email' && respuestas[String(campo.id)]) {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(respuestas[String(campo.id)])) {
          errors[String(campo.id)] = 'Ingresa un email válido'
        }
      }
    }
    setFieldErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleStandardSubmit = async () => {
    if (!encuesta || !uuid) return
    if (!validate()) return
    setSubmitting(true)
    try {
      const result = await submitEncuesta(uuid, {
        sesion_uuid: encuesta.sesion_uuid,
        respuestas,
        campo_principal_valor: campoPrincipalValor || undefined,
      })
      navigate(result.redirect_url, { replace: true })
    } catch {
      setError('Error al enviar la encuesta. Intenta nuevamente.')
      setSubmitting(false)
    }
  }

  // === MULTI-STEP LOGIC ===
  // step 0 = bienvenida, step 1..N = campos, último paso = resultados (en ResultadosPage)
  const totalPasos = encuesta ? encuesta.campos.length + 2 : 0 // bienvenida + campos + resultados
  const currentCampo = encuesta && currentStep >= 1 && currentStep <= encuesta.campos.length
    ? encuesta.campos[currentStep - 1]
    : null
  const isLastCampo = encuesta ? currentStep === encuesta.campos.length : false
  const progressPercentage = totalPasos > 0 ? Math.round(((currentStep + 1) / totalPasos) * 100) : 0

  const handleMultiStepNext = async () => {
    if (currentStep === 0) {
      // Bienvenida - validar nombre (siempre requerido en multi-step)
      if (campoPrincipalValor.trim().length < 2) {
        setFieldError('Este campo es obligatorio')
        return
      }
      // Mostrar modal instrucciones
      setShowInstrucciones(true)
      return
    }

    // Validar campo actual
    if (currentCampo?.requerido && !respuestas[String(currentCampo.id)]?.trim()) {
      setFieldError('Seleccione una opción')
      return
    }
    setFieldError('')

    if (isLastCampo) {
      // Enviar
      await handleMultiStepSubmit()
    } else {
      setCurrentStep((s) => s + 1)
    }
  }

  const handleMultiStepSubmit = async () => {
    if (!encuesta || !uuid) return
    setSubmitting(true)
    try {
      const result = await submitEncuesta(uuid, {
        sesion_uuid: encuesta.sesion_uuid,
        respuestas,
        campo_principal_valor: campoPrincipalValor || undefined,
      })
      navigate(result.redirect_url, { replace: true })
    } catch {
      setError('Error al enviar. Intenta nuevamente.')
      setSubmitting(false)
    }
  }

  // === RENDER ===
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #015b9a 0%, #b12063 100%)' }}>
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    )
  }

  if (error && !encuesta) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md text-center">
          <p className="text-red-500">{error}</p>
        </div>
      </div>
    )
  }

  if (!encuesta) return null

  // ==========================================
  // MULTI-STEP MODE
  // ==========================================
  if (encuesta.es_multi_step) {
    return (
      <div className="min-h-screen flex items-center justify-center p-3 sm:p-4" style={{ background: 'linear-gradient(135deg, #015b9a 0%, #b12063 100%)' }}>
        <div className="bg-white/95 backdrop-blur-xl border border-white/20 shadow-2xl rounded-2xl p-4 sm:p-6 lg:p-8 max-w-3xl w-full animate-in fade-in slide-in-from-bottom-4 duration-500">
          {/* Logo */}
          {encuesta.imagen && (
            <div className="flex justify-center mb-4 sm:mb-6">
              <img src={encuesta.imagen} alt={encuesta.titulo} className="max-w-48 w-auto h-16 sm:max-w-64 sm:h-20 lg:max-w-80 lg:h-24 object-contain" />
            </div>
          )}

          {/* Step indicators */}
          <div className="flex items-center justify-center mb-3 sm:mb-4 px-1 sm:px-4 lg:px-6">
            <div className="flex items-center">
              {Array.from({ length: totalPasos }).map((_, i) => (
                <div key={i} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <span className={`flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full border-2 text-[10px] sm:text-xs font-semibold mb-1 ${
                      i + 1 <= currentStep + 1 ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 dark:border-gray-700 text-gray-400'
                    }`}>
                      {i + 1}
                    </span>
                    <span className={`text-xs font-medium hidden sm:block ${
                      i + 1 <= currentStep + 1 ? 'text-blue-600' : 'text-gray-400'
                    }`}>
                      Paso {i + 1}
                    </span>
                  </div>
                  {i < totalPasos - 1 && (
                    <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 mx-0.5 sm:mx-2 lg:mx-4" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Progress bar */}
          <div className="bg-blue-100 h-2 rounded-full overflow-hidden mb-6 sm:mb-8">
            <div
              className="h-full bg-gradient-to-r from-blue-600 to-blue-700 transition-all duration-300 rounded-full"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>

          {/* Bienvenida (paso 0) */}
          {currentStep === 0 && (
            <div className="text-center space-y-6">
              <h1 className="text-xl sm:text-3xl lg:text-4xl font-bold text-gray-900">{encuesta.titulo}</h1>
              {encuesta.descripcion && <p className="text-lg text-gray-600">{encuesta.descripcion}</p>}

              <div className="max-w-md mx-auto text-left mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                  <Stethoscope className="mr-2 h-4 w-4" />
                  {encuesta.campo_principal_etiqueta || 'Nombre de su médico'}
                </label>
                <input
                  type="text"
                  value={campoPrincipalValor}
                  onChange={(e) => { setCampoPrincipalValor(e.target.value); setFieldError('') }}
                  placeholder={encuesta.campo_principal_placeholder || 'Ingrese el nombre de su médico'}
                  className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-700 rounded-lg focus:border-blue-600 focus:outline-none transition"
                />
                {fieldError && <p className="text-red-600 text-sm mt-1">{fieldError}</p>}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-6">
                <p className="text-blue-800 font-medium mb-1 sm:mb-2 text-sm sm:text-base">
                  <Info className="mr-1 sm:mr-2 h-4 w-4 sm:h-5 sm:w-5 inline" />
                  Esta encuesta tiene {encuesta.campos.length} preguntas
                </p>
                <p className="text-blue-600 text-xs sm:text-sm">
                  Tiempo estimado: {encuesta.campos.length} minutos
                </p>
              </div>

              <button
                type="button"
                onClick={handleMultiStepNext}
                disabled={campoPrincipalValor.trim().length < 2}
                className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
              >
                Comenzar
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
            </div>
          )}

          {/* Campo actual (paso 2+) */}
          {currentCampo && (
            <div className="space-y-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6">
                {currentCampo.etiqueta}
              </h2>

              <FieldRenderer
                campo={currentCampo}
                value={respuestas[String(currentCampo.id)] || ''}
                onChange={(val) => handleRespuestaChange(currentCampo.id, val)}
                error={fieldError}
                verticalScale
              />

              <div className="mt-8 flex justify-between items-center">
                {currentStep > 1 ? (
                  <button
                    type="button"
                    onClick={() => { setCurrentStep((s) => s - 1); setFieldError('') }}
                    className="text-blue-600 hover:text-blue-700 font-semibold flex items-center"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />Anterior
                  </button>
                ) : <div />}

                <button
                  type="button"
                  onClick={handleMultiStepNext}
                  disabled={submitting}
                  className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all flex items-center whitespace-nowrap disabled:opacity-50"
                >
                  {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {isLastCampo ? 'Enviar' : 'Siguiente'}
                  {isLastCampo ? <SendHorizonal className="ml-2 h-4 w-4 flex-shrink-0" /> : <ArrowRight className="ml-2 h-4 w-4 flex-shrink-0" />}
                </button>
              </div>
            </div>
          )}

          {/* Footer */}
          {encuesta.footer && (
            <div className="mt-6 pt-4 border-t border-gray-200 text-center">
              <p className="text-sm text-gray-600">{encuesta.footer}</p>
            </div>
          )}
        </div>

        {/* Modal Instrucciones */}
        {showInstrucciones && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4">
            <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto animate-in fade-in slide-in-from-bottom-4 duration-300">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 sm:p-6 rounded-t-2xl">
                <h3 className="text-lg sm:text-2xl font-bold flex items-center">
                  <Info className="mr-2 sm:mr-3 h-5 w-5 sm:h-7 sm:w-7" />
                  Instrucciones
                </h3>
              </div>

              <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                <div className="bg-blue-50 border-l-4 border-blue-600 p-3 sm:p-4 rounded">
                  <h4 className="font-semibold text-blue-900 mb-2 sm:mb-3 text-sm sm:text-lg">Por favor, lea cuidadosamente:</h4>
                  <ol className="space-y-2 sm:space-y-3 text-sm sm:text-base text-gray-700">
                    <li className="flex items-start">
                      <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs sm:text-sm font-bold mr-2 sm:mr-3 mt-0.5">1</span>
                      <span>Haga clic en la opción que mejor represente a cada una de las preguntas siguientes.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs sm:text-sm font-bold mr-2 sm:mr-3 mt-0.5">2</span>
                      <span>Marque sólo <strong>UNA</strong> opción por pregunta.</span>
                    </li>
                    <li className="flex items-start">
                      <span className="flex-shrink-0 w-5 h-5 sm:w-6 sm:h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs sm:text-sm font-bold mr-2 sm:mr-3 mt-0.5">3</span>
                      <span>Entienda que no hay respuestas correctas o incorrectas a estas preguntas.</span>
                    </li>
                  </ol>
                </div>

                {encuesta.descripcion && (
                  <div className="bg-yellow-50 border-l-4 border-yellow-500 p-3 sm:p-4 rounded">
                    <h4 className="font-semibold text-yellow-900 mb-1 sm:mb-2 flex items-center text-sm sm:text-base">
                      <AlertCircle className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                      Definición importante:
                    </h4>
                    <p className="text-sm sm:text-base text-gray-700">{encuesta.descripcion}</p>
                  </div>
                )}

                <div className="flex justify-end space-x-2 sm:space-x-3 pt-3 sm:pt-4">
                  <button
                    type="button"
                    onClick={() => setShowInstrucciones(false)}
                    className="px-4 sm:px-6 py-2.5 sm:py-3 border-2 border-gray-300 dark:border-gray-700 text-gray-700 rounded-lg text-sm sm:text-base font-semibold hover:bg-gray-50 transition-all"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowInstrucciones(false); setCurrentStep(1); setFieldError('') }}
                    className="px-4 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg text-sm sm:text-base font-semibold hover:shadow-lg transition-all inline-flex items-center whitespace-nowrap"
                  >
                    He leído y entiendo
                    <ArrowRight className="ml-2 h-4 w-4 flex-shrink-0" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // ==========================================
  // STANDARD FORM MODE
  // ==========================================
  return (
    <div className="min-h-screen" style={{ background: '#f5f5f5', fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}>
      <div className="max-w-[600px] mx-auto px-5 py-10 text-center">

        {!started ? (
          /* Pantalla de introducción */
          <div>
            {encuesta.imagen && (
              <div className="mb-5">
                <img src={encuesta.imagen} alt={encuesta.titulo} className="max-h-[200px] w-auto mx-auto" />
              </div>
            )}

            <h1 className="text-[1.8rem] font-semibold text-gray-800 mb-4 leading-tight">{encuesta.titulo}</h1>

            {encuesta.descripcion && (
              <div className="text-base text-gray-500 mb-5 leading-relaxed">{encuesta.descripcion}</div>
            )}

            {encuesta.campo_principal_activo && (
              <div className="text-[2.2rem] font-bold mb-6 leading-tight" style={{
                background: 'linear-gradient(45deg, #4285f4, #34a853, #fbbc04, #ea4335)',
                backgroundSize: '300% 300%',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                animation: 'gradientShift 3s ease-in-out infinite',
              }}>
                {encuesta.campo_principal_etiqueta}
              </div>
            )}

            <button
              type="button"
              onClick={() => setStarted(true)}
              className="inline-block mb-2.5 text-white font-semibold text-lg py-4 px-10 rounded-lg cursor-pointer transition-all hover:no-underline"
              style={{ background: '#4285f4', border: 'none' }}
            >
              Comenzar
            </button>

            <div className="text-gray-500 text-sm flex items-center justify-center mt-1">
              Toma 45 seg
            </div>
          </div>
        ) : (
          /* Formulario */
          <div className="text-left">
            {/* Logo pequeño */}
            {encuesta.imagen && (
              <div className="text-center mb-0">
                <img src={encuesta.imagen} alt="" className="max-h-[100px] w-auto opacity-90 rounded-lg mx-auto" />
              </div>
            )}

            {/* Progress bar */}
            <div className="sticky top-0 bg-[#f5f5f5] px-10 pb-5 border-b border-gray-200 z-50 max-w-[600px] mx-auto">
              <div className="flex justify-between items-center mb-2">
                <small className="text-gray-500">Progreso</small>
                <small className="text-gray-500">{filledCount} / {encuesta.campos.length}</small>
              </div>
              <div className="h-1 bg-gray-200 rounded-full">
                <div
                  className="h-full rounded-full transition-all duration-300"
                  style={{ width: `${encuesta.campos.length > 0 ? (filledCount / encuesta.campos.length) * 100 : 0}%`, background: '#4285f4' }}
                />
              </div>
            </div>

            {/* Campos */}
            <div className="py-10 px-10 max-w-[600px] mx-auto">
              {encuesta.campos.map((campo) => (
                <div key={campo.id} className="mb-8">
                  <FieldRenderer
                    campo={campo}
                    value={respuestas[String(campo.id)] || ''}
                    onChange={(val) => handleRespuestaChange(campo.id, val)}
                    error={fieldErrors[String(campo.id)]}
                  />
                </div>
              ))}
            </div>

            {/* Submit section */}
            <div className="bg-[#f5f5f5] px-10 py-10 text-center border-t border-gray-200 max-w-[600px] mx-auto">
              {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

              <button
                type="button"
                onClick={handleStandardSubmit}
                disabled={submitting}
                className="text-white font-semibold text-lg py-4 px-10 rounded-md cursor-pointer transition-all border-none disabled:bg-gray-500 disabled:cursor-not-allowed"
                style={{ background: submitting ? '#6c757d' : '#4285f4' }}
              >
                {submitting && <Loader2 className="inline mr-2 h-4 w-4 animate-spin" />}
                <SendHorizonal className="inline mr-2 h-4 w-4" />
                Enviar Respuestas
              </button>

              <p className="text-gray-500 mt-3 mb-0 text-sm">
                Tus respuestas son confidenciales y seguras
              </p>
            </div>

            {/* Footer */}
            {encuesta.footer && (
              <div className="mt-4 pt-3 border-t text-center">
                <p className="text-gray-500 mb-0 text-sm">{encuesta.footer}</p>
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </div>
  )
}
